import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import {
  delayForIndexing,
  exists,
  getDateTimestamp,
  getDateTimestampLocal,
  SystemTag,
} from "@examspark/common";
import {
  Button,
  Form,
  Modal,
  ModalProps,
  SelectInput,
  TextInput,
  toast,
  Toolbar,
  VSpace,
} from "@examspark/common-ui";
import {
  Classroom,
  IDSK,
  Maybe,
  Resource,
  ResourceVisibilityType,
  Unit,
} from "@examspark/types";

import { useQueryGetExam, useQuerySearchExams } from "../../api";
import { useMutationAddExamToClassroom } from "../../api/operations/activity";
import { useQueryGetUserClassrooms } from "../../api/operations/classroom";
import { useModal } from "../../hooks/useModal";
import { useAppContext } from "../../providers/AppContext";
import { ClassroomUnitSelect } from "../classrooms/ClassroomUnitSelect";
import NewClassroomModal from "../classrooms/modals/NewClassroomModal";

const AssignExamToClassroomModal = ({
  exam,
  onAssign,
  limitClassroomsToCourse,
  ...props
}: ModalProps & {
  exam?: Resource;
  requireClassroomUnit?: boolean;
  classroom?: Maybe<Classroom>;
  unit?: Maybe<Unit>;
  limitClassroomsToCourse?: IDSK;
  onAssign?: (data: {
    exam: Resource;
    classroom: Classroom;
    examConfig: IDSK;
    unit?: Unit;
  }) => void;
}) => {
  const [state] = useAppContext();
  const assignToClassroom = useMutationAddExamToClassroom();
  const newClassroomModal = useModal();
  const navigate = useNavigate();
  const [isAssigning, setIsAssigning] = useState(false);
  const [name, setName] = useState(`${exam?.name} ${getDateTimestamp()}`);
  const [classroomSelect, setClassroomSelect] = useState<Classroom | null>(
    null,
  );
  const [unitSelect, setUnitSelect] = useState<Unit | null>(null);
  const [version, setVersion] = useState("");

  const { data: _exam } = useQueryGetExam({
    id: exam?.id,
    sk: "exam#v0",
  });

  useEffect(() => {
    if (!_exam) return;
    setName(`${_exam.name} ${getDateTimestampLocal()}`);
  }, [_exam, _exam?.name]);

  const isPrivate = _exam?.visibility === ResourceVisibilityType.Private;
  const isCourseBasedClassroom =
    state.app?.config?.capabilities?.courseBasedClassroomsEnabled;

  const { data: classroomsData, refetch: refetchClassrooms } =
    useQueryGetUserClassrooms(state.workspaceUser?.id, state.user?.id);

  const classrooms = useMemo(() => {
    return (
      classroomsData
        ?.filter(exists)
        .map((c) => c.classroom)
        .filter(exists)
        // filter out classrooms to the current course
        .filter((c) => {
          if (!limitClassroomsToCourse) {
            return true;
          }
          return c.courseId === limitClassroomsToCourse.id;
        })
        .map((c) => ({
          label: c.name ?? "",
          value: c.id,
          classroom: c,
        }))
    );
  }, [classroomsData, limitClassroomsToCourse]);

  useEffect(() => {
    setName(`${exam?.name} ${getDateTimestampLocal()}`);
  }, [exam]);

  useEffect(() => {
    if (props.classroom) {
      setClassroomSelect(props.classroom);
    } else if (classrooms?.length) {
      setClassroomSelect(classrooms[0].classroom);
    }
  }, [classrooms, props.classroom]);

  const handleAssign = async () => {
    const classroom = props.classroom ?? classroomSelect;
    const unit = props.unit ?? unitSelect;

    if (!classroom || !exam) {
      toast.error("Error adding to classroom");
      console.error("Missing required params:", {
        classroom,
        exam,
      });
      return;
    }

    try {
      if (!state.appLicense?.appKey) {
        throw new Error("App key not found");
      }
      if (props.requireClassroomUnit && !unit) {
        throw new Error("Unit is required");
      }

      setIsAssigning(true);
      const assignResult = await assignToClassroom.mutateAsync({
        exam: version ? { id: version, sk: "exam#v0" } : exam,
        classroom,
        classroomUnit: unit ?? undefined,
        name,
        // if this is a user's private exam, we can assign onto this exam,
        // otherwise we need to make a copy.
        useOriginalExam: isPrivate || !!version,
      });

      if (!assignResult) {
        toast.error("Failed to add assessment to classroom");
        return;
      }

      await delayForIndexing();
      toast.success("Assessment added to classroom");
      onAssign?.({
        exam,
        classroom: classroom,
        examConfig: assignResult,
        unit: unit ?? undefined,
      });

      if (isCourseBasedClassroom) {
        if (classroom.id) {
          if (unit) {
            navigate(
              `/classroom/${classroom.id}/unit/${unit.id}/assignment/${assignResult.id}`,
            );
          }
        }
      }
      // if not, we can reset the form
    } catch (e) {
      console.error(e);
      toast.error("Failed to add assessment to classroom");
    } finally {
      setIsAssigning(false);
    }
  };

  let isValid = true;
  if (
    props.requireClassroomUnit &&
    // unit was not supplied by props
    !(props.classroom && props.unit) &&
    // nor was it selected manually by user
    !(classroomSelect && unitSelect)
  ) {
    isValid = false;
  }

  // if this is a course-based classroom and we can't determine a unit via props
  // or via the select element, we aren't valid yet.
  if (isCourseBasedClassroom && !unitSelect && !props.unit) {
    isValid = false;
  }

  const requireClassroomUnit =
    isCourseBasedClassroom ?? props.requireClassroomUnit ?? false;

  return (
    <>
      <Modal
        {...props}
        title="Assign To Classroom"
        onAfterOpen={() => {
          setClassroomSelect(classrooms?.[0]?.classroom ?? null);
        }}
        buttons={
          <>
            <Toolbar>
              {/* {!lti && (
                <Button
                  title="New Classroom"
                  onClick={() => newClassroomModal.open()}
                  disabled={!name || isAssigning}
                >
                  New Classroom
                </Button>
              )} */}
              <Button
                title="Cancel"
                onClick={props.onRequestClose}
                disabled={!classroomSelect || !name || isAssigning}
              >
                Cancel
              </Button>
              <Button
                title="Assign to Classroom"
                color="primary"
                onClick={handleAssign}
                disabled={!_exam || !name || isAssigning || !isValid}
                isLoading={isAssigning}
              >
                Assign
              </Button>
            </Toolbar>
          </>
        }
      >
        <Modal.PaddedBody>
          <Form.FieldList>
            <Form.Field label="Assignment Name">
              <TextInput
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Form.Field>
            {(!props.classroom || !props.unit) && (
              <>
                {!requireClassroomUnit ? (
                  <Form.Field label="Classroom">
                    <SelectInput
                      options={classrooms}
                      value={classroomSelect?.id}
                      onChange={(v) => {
                        setClassroomSelect(
                          classrooms?.find((c) => c.value === v?.value)
                            ?.classroom ?? null,
                        );
                      }}
                    />
                  </Form.Field>
                ) : (
                  <ClassroomUnitSelect
                    unit={unitSelect}
                    classroom={classroomSelect}
                    onClassroomChange={(v) => setClassroomSelect(v)}
                    onUnitChange={(v: any) => setUnitSelect(v)}
                  />
                )}
              </>
            )}
            {_exam && !isPrivate && (
              <VersionPicker
                value={version}
                onChange={(examId) => setVersion(examId)}
                examId={_exam.id}
              />
            )}
          </Form.FieldList>
        </Modal.PaddedBody>
      </Modal>
      <NewClassroomModal
        {...newClassroomModal.modalProps}
        // todo: parameterize
        // hideCourseInputs
        hideCourseInput={false}
        hideSubjectInput={false}
        hideGradeInput={false}
        onCreate={() => {
          newClassroomModal.close();
          refetchClassrooms();
        }}
      />
    </>
  );
};

export default AssignExamToClassroomModal;

type StyledOptionProps = {
  selected?: boolean;
};

const StyledOption = styled.div`
  padding: 0.7rem;
  cursor: pointer;
  display: flex;
  border-radius: 0.5rem;
  transition: background-color 0.2s;
  gap: 1rem;
  border: 1px solid transparent;

  input {
    margin: 0;
    display: block;
  }

  &:hover {
    background-color: #f0f0f0;
  }

  ${(props: StyledOptionProps) =>
    props.selected &&
    `
    background-color: #e0f7fa;
    border: 1px solid #00bcd4;

    &:hover {
      background-color: #e0f7fa;
      border: 1px solid #00bcd4;
    }
  `}
`;
const StyledHelperText = styled.div`
  color: #666;
`;

const StyledLabel = styled.div`
  color: #666;
`;

const VersionPicker = (props: {
  examId: string;
  value?: string;
  onChange: (examId: string) => void;
}) => {
  const value = props.value ?? "";
  const [appState] = useAppContext();
  const { data } = useQuerySearchExams({
    filter: {
      visibility: {
        eq: ResourceVisibilityType.Private,
      },
      creatorId: {
        eq: appState.user?.id,
      },
      tags: {
        eq: `${SystemTag.OriginExamId}#${props.examId}`,
      },
    },
  });

  const allExams = useMemo(() => {
    return (
      data?.pages
        .flatMap((page) => page.items)
        .filter((exam) => exam.id !== props.examId) ?? []
    );
  }, [data?.pages, props.examId]);

  useEffect(() => {
    if (!allExams?.length) return;
    props.onChange?.(allExams[0].id ?? "");
    // console.log("changing", allExams[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allExams]);

  return (
    <VSpace>
      <StyledLabel>
        Shared Assessments must be copied into your library before being
        assigned. Choose an existing copy from your library below, or create a
        new version.
      </StyledLabel>
      <VSpace size="0.2rem">
        {allExams
          .map((exam) => ({
            label: exam.name ?? "",
            value: exam.id,
            exam,
          }))
          .map((exam) => (
            <StyledOption
              key={exam.value}
              onClick={() => props.onChange(exam.exam.id)}
              selected={exam.exam.id === value}
            >
              <div style={{ padding: "0.1rem 0" }}>
                <input
                  type="radio"
                  value={exam.exam.id}
                  checked={exam.exam.id === value}
                />
              </div>
              <VSpace size="0.4rem">
                <div>{exam.label}</div>
                <StyledHelperText>
                  Updated {(exam.exam.updatedAt as string) ?? ""}
                </StyledHelperText>
              </VSpace>
            </StyledOption>
          ))}

        <StyledOption
          selected={value === ""}
          onClick={() => props.onChange("")}
        >
          <div style={{ padding: "0.1rem 0" }}>
            <input type="radio" checked={value === ""} />
          </div>
          <VSpace size="0.4rem">
            <div>Create a New Version</div>
            <StyledHelperText>
              Make a new copy of this assessment for your library
            </StyledHelperText>
          </VSpace>
        </StyledOption>
      </VSpace>
    </VSpace>
  );
};
